// Domain types for the Penetration Report V2 preview page.
// Convention: `as const` objects + derived string-literal union

export const Solution = {
  PURCHEK: 'purchek',
  CONTAINMENT: 'containment',
  CART_MANAGER: 'cart manager',
  PAIRED_WHEELS: 'paired wheels',
  FACEFIRST: 'facefirst',
} as const;
export type SolutionType = typeof Solution[keyof typeof Solution];

export const Channel = {
  GROCERY: 'Grocery',
  CLUB: 'Club',
  DRUG_AND_PHARMACY: 'Drug & Pharmacy',
  DOLLAR_STORE: 'Dollar Store',
} as const;
export type ChannelType = typeof Channel[keyof typeof Channel];

export const BreakdownDimension = {
  BRAND: 'brand',
  SOLUTION: 'solution',
  CHANNEL: 'channel',
  STATE: 'state',
} as const;
export type BreakdownDimensionType = typeof BreakdownDimension[keyof typeof BreakdownDimension];

export const SignalTileKey = {
  HIGH_CRIME: 'high_crime',
  HIGH_PURCHEK_CONCENTRATION: 'high_purchek_concentration',
  CART_ORDINANCE: 'cart_ordinance',
} as const;
export type SignalTileKeyType = typeof SignalTileKey[keyof typeof SignalTileKey];

export const MapTimeView = {
  AS_OF: 'as_of',
  COMPARE: 'compare',
  CHANGES: 'changes',
} as const;
export type MapTimeViewType = typeof MapTimeView[keyof typeof MapTimeView];

export const MapPointFilter = {
  ALL: 'all',
  CUSTOMERS: 'customers',
  NON_CUSTOMERS: 'non_customers',
} as const;
export type MapPointFilterType = typeof MapPointFilter[keyof typeof MapPointFilter];

export const PointClassification = {
  CUSTOMER: 'customer',
  PROSPECT: 'prospect',
  CONVERTED: 'converted',
  LOST_TO_NON_CUSTOMER: 'lost_to_non_customer',
} as const;
export type PointClassificationType = typeof PointClassification[keyof typeof PointClassification];

/** Sentinel value for the "All brands / All channels / All solutions" dropdown option. */
export const ALL_FILTER_VALUE = '__all__' as const;

export interface Period {
  year: number;
  month: number; // 1-12
}

/**
 * One target location in the addressable market. Represents either an existing
 * customer or a prospect. This is the single seeded record shape that every
 * derived view (hero, trend, signal tiles, breakdown table, footprint map) is
 * computed from — swapping seed data for a live fetch only needs to keep
 * producing this shape.
 */
export interface MarketRecord {
  id: string;
  brand: string;
  channel: ChannelType;
  state: string;
  stateCode: string;
  lat: number;
  lng: number;
  isCustomerAsOf: boolean;
  isCustomerPrior: boolean;
  solutionsInstalled: SolutionType[];
  solutionsInstalledPrior: SolutionType[];
  highCrime: boolean;
  highPurchekConcentration: boolean;
  cartOrdinance: boolean;
}

export interface TrendPoint {
  /** Calendar label for this point on the "as of" timeline — used as the x-axis category. */
  monthLabel: string;
  /** Calendar label for the same index on the independently-anchored "compare with" timeline — shown in the tooltip. */
  priorMonthLabel: string;
  currentPct: number;
  priorPct: number;
}

/** Selectable trend-chart window lengths (months), counting back from the "As of" / "Compare with" periods. */
export const TREND_RANGE_OPTIONS: readonly number[] = [3, 6, 12, 24];

export interface HeroMetrics {
  currentPct: number;
  priorPct: number;
  deltaPct: number;
  acquiredCount: number;
  targetCount: number;
  remainingCount: number;
  longTermTargetPct: number;
}

export interface BreakdownRow {
  key: string;
  label: string;
  currentPct: number;
  priorPct: number;
  deltaPct: number;
  acquiredCount: number;
  targetCount: number;
}

export interface SignalTileData {
  key: SignalTileKeyType;
  label: string;
  currentPct: number;
  priorPct: number;
  deltaPct: number;
  acquiredCount: number;
}

export interface PenetrationV2Filters {
  brand: string | typeof ALL_FILTER_VALUE;
  channel: ChannelType | typeof ALL_FILTER_VALUE;
  solution: SolutionType | typeof ALL_FILTER_VALUE;
}
